import { Shield, Lock, TrendingUp, Eye, CheckCircle2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const trustBadges = [
  {
    icon: Shield,
    label: "Protection FGDR",
    description: "Mécanisme de protection des dépôts",
  },
  {
    icon: Lock,
    label: "Compte-titres sécurisé",
    description: "Protection jusqu'à 70 000€ par enfant",
  },
  {
    icon: Eye,
    label: "Transparence totale",
    description: "Suivez chaque centime en temps réel",
  },
  {
    icon: TrendingUp,
    label: "Performance historique",
    description: "Indices mondiaux : +7-8%/an sur 30 ans",
  },
];

const faqs = [
  {
    question: "L'argent est-il vraiment sécurisé ?",
    answer:
      "Oui. Nous travaillons avec des banques partenaires européennes agréées ACPR. Vos dépôts sont garantis jusqu'à 100 000€ par le Fonds de Garantie des Dépôts et de Résolution (FGDR) et vos titres jusqu'à 70 000€. Le même niveau de sécurité qu'une banque traditionnelle française.",
  },
  {
    question: "Où est investi l'argent exactement ?",
    answer:
      "Dans des ETF diversifiés (indices boursiers mondiaux) via nos partenaires bancaires agréés AMF. Vous pouvez voir à tout moment la composition exacte de votre portefeuille et son évolution. Pas de produits complexes, juste de l'investissement indiciel transparent conforme aux normes MiFID II. Les performances passées ne préjugent pas des performances futures, mais historiquement les indices mondiaux ont affiché une croissance moyenne annuelle de 7-8% sur le long terme.",
  },
  {
    question: "Les souvenirs sont-ils vraiment conservés à vie ?",
    answer:
      "Oui, absolument. Tous les albums-moments (photos, messages, vidéos) restent accessibles sans limite de temps. Même si vous arrêtez d'investir, les souvenirs restent. C'est votre héritage émotionnel permanent.",
  },
  {
    question: "Y a-t-il un montant minimum pour commencer ?",
    answer:
      "Non. Vous pouvez créer le compte Legatia de votre enfant et lancer votre première cagnotte-moment dès 50€. L'important, c'est la régularité : de petits montants investis sur 18 ans ont un impact énorme grâce aux intérêts composés.",
  },
  {
    question: "D'où vient la projection de 7-8% par an ?",
    answer:
      "Cette projection est basée sur la performance historique du MSCI World Index, un indice boursier qui suit les 1 600 plus grandes entreprises mondiales. Sur les 30 dernières années (1994-2024), cet indice a affiché une croissance annuelle moyenne de 7-8%. IMPORTANT : les performances passées ne garantissent pas les résultats futurs. La bourse fluctue à court terme mais a historiquement montré une croissance constante sur le long terme (18 ans dans notre cas).",
  },
];

const TrustSection = () => {
  return (
    <section className="section-padding bg-babyBlue-light/10">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Confier les moments et l'argent en toute sérénité</h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            Protection FGDR • Transparence totale • Partenaires bancaires agréés
          </p>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {trustBadges.map((badge, index) => {
            const Icon = badge.icon;
            const badgeColors = ['bg-growth', 'bg-future', 'bg-babyBlue', 'bg-sunrise'];
            return (
              <div
                key={index}
                className="flex flex-col items-center p-6 rounded-xl bg-white shadow-md hover:shadow-xl transition-all group"
              >
                <div className={`w-16 h-16 rounded-full ${badgeColors[index]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>
                <span className="text-sm font-bold text-center mb-2">{badge.label}</span>
                <span className="text-xs text-muted-foreground text-center">{badge.description}</span>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold mb-2">Vos questions, nos réponses</h3>
            <p className="text-muted-foreground">Tout ce que vous devez savoir sur la sécurité et le fonctionnement</p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-tenderness-light/20 rounded-xl px-6 border border-tenderness-light shadow-sm hover:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">
                  <span className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-8">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
